import { useState } from "react";
import {
  FEEDBACK_PRIORITIES,
  FEEDBACK_PRIORITY_ORDER,
} from "@/lib/feedback-priority";
import { Navigate } from "react-router-dom";
import { useRBAC } from "@/hooks/use-rbac";
import {
  useAdminFeedback,
  useUpdateFeedbackStatus,
  useSoftDeleteFeedback,
  useAddFeedbackReply,
  type FeedbackFilters,
  type FeedbackStatus,
} from "@/hooks/use-feedback-admin";
import { FeedbackCard } from "@/components/feedback/FeedbackCard";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SrIcon } from "@/components/ui/SrIcon";

export default function FeedbackAdminPage() {
  const { isAdmin } = useRBAC();
  const [typeFilter, setTypeFilter] = useState<FeedbackFilters["type"]>("all");
  const [statusFilter, setStatusFilter] = useState<FeedbackFilters["status"]>("active");
  // VIB-2085: prioritet er det man sorterer etter i praksis — blokkerende
  // saker først, resten når det er tid.
  const [priorityFilter, setPriorityFilter] = useState<FeedbackFilters["priority"]>("all");
  const { data: items = [], isLoading } = useAdminFeedback({
    type: typeFilter,
    status: statusFilter,
    priority: priorityFilter,
  });
  const updateStatus = useUpdateFeedbackStatus();
  const softDelete = useSoftDeleteFeedback();
  const addReply = useAddFeedbackReply();

  if (!isAdmin) return <Navigate to="/" replace />;

  const pending = updateStatus.isPending || addReply.isPending;

  // Statusbytte oppdateres FØR svaret skrives, så e-posten til melder leser ny status.
  const handleSubmit = async (id: string, status: FeedbackStatus, message: string) => {
    try {
      await updateStatus.mutateAsync({ id, status });
      if (message) await addReply.mutateAsync({ feedbackId: id, body: message });
    } catch {
      // Feilmeldinger vises via toast i hookene.
    }
  };

  return (
    <div className="page-container animate-fade-in">
      <div className="flex items-center gap-2 mb-6">
        <SrIcon name="megaphone" className="w-6 h-6 text-primary" />
        <h1 className="font-display text-2xl">In-app feedback</h1>
      </div>

      <Card className="mb-4">
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Filter</span>
          <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v as FeedbackFilters["type"])}>
            <SelectTrigger className="w-[160px] h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle typer</SelectItem>
              <SelectItem value="bug">Kun bugs</SelectItem>
              <SelectItem value="feature">Kun forslag</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as FeedbackFilters["status"])}>
            <SelectTrigger className="w-[180px] h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Aktive (nye)</SelectItem>
              <SelectItem value="all">Alle (ekskl. slettede)</SelectItem>
              <SelectItem value="new">Kun nye</SelectItem>
              <SelectItem value="acknowledged">Kun løste</SelectItem>
              <SelectItem value="closed">Kun avsluttede</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={priorityFilter}
            onValueChange={(v) => setPriorityFilter(v as FeedbackFilters["priority"])}
          >
            <SelectTrigger className="w-[200px] h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle prioriteter</SelectItem>
              {FEEDBACK_PRIORITY_ORDER.map((p) => (
                <SelectItem key={p} value={p}>
                  {FEEDBACK_PRIORITIES[p].label}
                </SelectItem>
              ))}
              <SelectItem value="unset">Uten prioritet</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex-1" />
          <span className="text-xs text-muted-foreground">{items.length} {items.length === 1 ? "rad" : "rader"}</span>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <SrIcon name="loader2" className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            Ingen feedback matcher filtrene.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {items.map((item) => (
            <FeedbackCard
              key={item.id}
              item={item}
              pending={pending}
              onSubmit={(status, message) => handleSubmit(item.id, status, message)}
              onDelete={() => softDelete.mutate(item.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
